<template>
  <div class="animate-fade-in-right">
    <!-- Header -->
    <header class="mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
      <div>
        <div class="flex items-center gap-4 mb-2">
          <button @click="$router.push({ name: 'events' })"
            class="bg-white/10 hover:bg-white/20 p-2 rounded-xl text-white transition-colors backdrop-blur-md border border-white/10">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 class="text-4xl font-bold text-white tracking-tight drop-shadow-sm">
            {{ eventById[0]?.eventName || t('Loading...') }}
          </h2>
        </div>
        <div class="flex items-center gap-2 text-white/70 ml-14 font-medium">
          <span class="bg-amber-500/20 text-amber-300 px-3 py-1 rounded-full text-xs uppercase tracking-wider border border-amber-500/30">{{ t('Expense List') }}</span>
          <span>•</span>
          <span>{{ expenses.length }} {{ t('Recorded Expenses') }}</span>
        </div>
      </div>

      <button @click="handlePopOpenform('add')"
        class="group relative overflow-hidden bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-bold py-3 px-8 rounded-2xl shadow-[0_4px_20px_rgba(245,158,11,0.4)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_8px_30px_rgba(245,158,11,0.5)]">
        <span class="flex items-center gap-2 relative z-10">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          {{ t('Add New Expense') }}
        </span>
      </button>
    </header>

    <!-- Summary Cards -->
    <div v-if="currencyTotals.length > 0" class="mb-10 flex flex-wrap justify-center gap-4">
      <div v-for="item in currencyTotals" :key="item.currency"
        class="relative min-w-[200px] bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-6 shadow-[0_8px_32px_rgba(0,0,0,0.2)] group hover:bg-white/15 transition-all duration-500 hover:-translate-y-1">
        <div class="absolute inset-0 rounded-3xl bg-gradient-to-br from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div class="flex flex-col items-center text-center relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <div class="w-2 h-2 rounded-full bg-amber-400"></div>
            <span class="text-white/40 text-[10px] font-black uppercase tracking-[0.2em]">{{ t('Total ' + item.currency + ' Expense') }}</span>
          </div>
          <div class="text-3xl font-black text-white tracking-tighter">
            {{ numeralFormat(item.total, '0,0[.]00') }}
          </div>
          <div class="mt-3 w-10 h-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 opacity-50 group-hover:w-16 transition-all duration-500"></div>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="mb-10 w-full max-w-xl">
      <div class="relative group">
        <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg class="w-5 h-5 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input v-model="searchQuery" type="text" :placeholder="t('Search expenses by title...')"
          class="w-full bg-white/5 backdrop-blur-xl border border-white/20 text-white placeholder-amber-200/50 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:bg-white/10 transition-all font-medium" />
      </div>
    </div>

    <!-- Expense Grid -->
    <div v-if="filteredExpenses.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="expense in filteredExpenses" :key="expense.id"
        class="group bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 p-6 transition-all duration-300 hover:bg-white/20 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.2)]">
        
        <div class="flex justify-between items-start mb-6">
          <div class="flex items-center gap-4">
            <div
              class="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-white/10 flex items-center justify-center text-white font-bold text-xl shadow-inner group-hover:scale-110 transition-transform">
              <i class="fa-solid fa-receipt text-amber-400"></i>
            </div>
            <div>
              <h3 class="text-xl font-bold text-white group-hover:text-amber-200 transition-colors truncate max-w-[150px]">
                {{ expense.title }}
              </h3>
              <p class="text-amber-300/80 text-sm font-medium">{{ t('Expense') }}</p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <button @click.stop="handlePopOpenform('edit', expense)"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/20 text-amber-300 hover:text-white transition-all border border-transparent hover:border-white/20"
              :title="t('Edit Expense')">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>

            <button @click.stop="handlePopDelete(expense)"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-red-500/20 text-red-300 hover:text-red-200 transition-all border border-transparent hover:border-red-500/30"
              :title="t('Delete Expense')">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        <div class="space-y-4">
          <div class="bg-black/20 rounded-2xl p-4 border border-white/5">
            <div class="text-white/40 text-xs uppercase tracking-wider mb-1">{{ t('Cost') }}</div>
            <div class="text-2xl font-black text-amber-400">
              {{ numeralFormat(expense.amount, '0,0[.]00') }} <span class="text-sm font-bold text-white/60 ml-1">{{ expense.currency }}</span>
            </div>
          </div>

          <div v-if="expense.description" class="text-white/60 text-sm leading-relaxed min-h-[3rem] italic">
            "{{ expense.description }}"
          </div>
          <div v-else class="text-white/20 text-sm italic min-h-[3rem]">
            {{ t('No description provided') }}
          </div>
        </div>

        <div class="mt-6 pt-4 border-t border-white/10 flex justify-between items-center">
          <div class="flex items-center gap-2 text-white/40 text-xs">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{{ formatDate2(expense.createdAt) }}</span>
          </div>
          <span class="text-amber-300/60 text-xs font-bold uppercase tracking-widest">{{ t('Receipt') }}</span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex flex-col items-center justify-center py-20 bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 text-white/30">
      <div class="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4 text-amber-400/30">
        <i class="fa-solid fa-receipt text-4xl"></i>
      </div>
      <p class="text-xl font-medium">{{ t('No expenses found') }}</p>
      <p class="text-sm">{{ t('Track your wedding spending by adding an expense.') }}</p>
    </div>
  </div>

  <component :is="currentComponent" :visible="openForm" :isEditDoc="isEditDoc" :doc="docData" :isFailed="isFailed"
    :elementName="elementName" :docId="deleteId" :elementCategory="elementCategory" :informMessage="informMessage"
    :showToast="showToast" @onClose="handleListenEmitEvent" />
</template>

<script setup>
import { ref, defineProps, onMounted, computed ,watch} from 'vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import { getCollectionQuery } from '@/composables/getCollection';
import { where, orderBy, documentId } from 'firebase/firestore';
import ExpenseForm from '@/components/Modal/ExpenseForm.vue';
import DeleteConfirmation from '@/components/DeleteConfirmation.vue';
import ToastedVue from '@/components/ToastedVue.vue';
import { projectAuth } from '@/firebase/config';
import { formatDate2 } from '@/composables/formatDate';
import { calculateExpirePlan } from '@/composables/calculateExpirePlan';

const props = defineProps({
  id: {
    type: String,
    required: true
  }
});

const searchQuery = ref("");
const openForm = ref(false);
const isEditDoc = ref(false);
const informMessage = ref("");
const showToast = ref(false);
const isFailed = ref(false);
const currentComponent = ref("");
const docData = ref(null);
const elementCategory = ref(null);
const elementName = ref(null);
const deleteId = ref(null);
const eventById = ref([]);
const expenses = ref([]);
const isExpired = ref(false);

const fetchEventById = async () => {
  try {
    await getCollectionQuery(
      "events",
      [where(documentId(), "==", props.id)],
      (data) => {
        eventById.value = data;
      },
      null,
      true
    );
  } catch (error) {
    console.error("failed to fetch event", error);
  }
};


const userByID = ref([]);
const fetchUserById = async () => {
  try {
    await getCollectionQuery(
      "users",
      [where(documentId(), "==", projectAuth.currentUser?.uid)],
      (data) => {
        userByID.value = data;
            if(userByID.value.length > 0){
          fetchPlanById();
        }
      },
      null,
      true
    );
  } catch (error) {
    console.log("failed to fetch event", error);
  }
};

const planByID = ref([]);
const fetchPlanById = async () => {
  try {
    await getCollectionQuery(
      "plans",
      [where(documentId(), "==", userByID.value[0]?.planId)],
      (data) => {
        planByID.value = data;

        
      },
      null,
      true
    );
  } catch (error) {
    console.log("failed to fetch event", error);
  }
};


const fetchExpenses = async () => {
  try {
    await getCollectionQuery(
      "expenses",
      [where("eventId", "==", props.id), orderBy("createdAt", "desc")],
      (data) => {
        expenses.value = data;
      },
      null,
      true
    );
  } catch (error) {
    console.error("failed to fetch expenses", error);
  }
};

watch(
  [userByID, planByID],
  ([newUser, newPlan]) => {
    if (newUser?.length > 0 && newPlan?.length > 0) {
      const user = newUser[0];
      const plan = newPlan[0];

      if (
        user.subscriptionStart &&
        plan.planDuration &&
        plan.planType
      ) {
        const res = calculateExpirePlan(
          user.subscriptionEnd,
          plan.planType
        );
        isExpired.value = res;

   
      } else {
        console.log("Waiting for subscription data...", {
          hasStart: !!user?.subscriptionStart,
          hasDuration: !!plan?.planDuration,
          hasType: !!plan?.planType
        });
      }
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

const filteredExpenses = computed(() => {
  return expenses.value.filter((exp) => {
    return exp.title
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase());
  });
});

const currencyTotals = computed(() => {
  const totals = {};
  expenses.value.forEach((exp) => {
    const cur = exp.currency || 'USD';
    const amt = parseFloat(exp.amount) || 0;
    if (!totals[cur]) {
      totals[cur] = 0;
    }
    totals[cur] += amt;
  });
  return Object.entries(totals).map(([currency, total]) => ({ currency, total }));
});

onMounted(async () => {
  fetchExpenses();
  fetchEventById();
  await fetchUserById();
  
});

const handlePopOpenform = (status, item) => {
  if (status === "add") {
    if (planByID.value[0]?.maxExpense === expenses.value.length) {
      isFailed.value = true;
      informMessage.value = t("You have reached the maximum number of expenses allowed on your current plan.");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    }

    if(isExpired.value){
      isFailed.value = true;
      informMessage.value =
        t("Your subscription has expired!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    }
    isEditDoc.value = false;
    docData.value = null;
    openForm.value = true;
    currentComponent.value = ExpenseForm;
  } else {
    if(isExpired.value){
      isFailed.value = true;
      informMessage.value =
        t("Your subscription has expired!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    }
    isEditDoc.value = true;
    docData.value = item;
    openForm.value = true;
    currentComponent.value = ExpenseForm;
  }
};

const handlePopDelete = (item) => {
  if(isExpired.value){
    isFailed.value = true;
    informMessage.value =
      t("Your subscription has expired!");
    showToast.value = true;
    currentComponent.value = ToastedVue;
    return;
  }
  deleteId.value = item.id;
  elementCategory.value = "expenses";
  elementName.value = item.expenseName;
  showToast.value = true;
  currentComponent.value = DeleteConfirmation;
};

const handleListenEmitEvent = (status) => {
  if (status === "create" || status === "update" || status === "delete") {
    informMessage.value = t(`Expense successfully ${status}!`);
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;
    currentComponent.value = ToastedVue;
  } else if (status === "close") {
    showToast.value = false;
    openForm.value = false;
    currentComponent.value = "";
  } else {
    showToast.value = false;
    openForm.value = false;
    currentComponent.value = "";
  }
};
</script>

<style scoped>
.animate-fade-in-right {
  animation: fadeInRight 0.5s ease-out forwards;
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
